import {
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  effect,
  inject,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';

import { ScrollableDirective } from '../scroll/scrollable.directive';
import { HorizontalScrollDirection } from '../../domain/direction';
import { IconComponent } from '../icon/icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { TabsComponent } from './tabs.component';

import type { OutputEmitterRef, Signal, WritableSignal, OnDestroy } from '@angular/core';
import type { TabComponent } from './tab.component';
import type { TabStatusChangeEvent } from './tabs.domain';

@Component({
  selector: 'td-wb-tabs-scroll',
  templateUrl: 'tabs-scroll.component.html',
  styleUrls: ['tabs-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, IconComponent, TranslatePipe, ScrollableDirective]
})
export class TabsScrollComponent<T> implements OnDestroy {
  private static readonly SCROLL_EDGE_THRESHOLD: number = 5;
  private static readonly DEBOUNCE_DELAY_MS: number = 500;

  readonly scrollableChange: OutputEmitterRef<boolean> = output<boolean>();
  readonly scrollable: Signal<ScrollableDirective> = viewChild(ScrollableDirective);

  readonly tabsQueryList: Signal<ReadonlyArray<TabsComponent<T>>> = contentChildren(TabsComponent, { descendants: true });

  readonly showPrevButton: WritableSignal<boolean> = signal(false);
  readonly showNextButton: WritableSignal<boolean> = signal(false);

  private readonly destroy: Subject<void> = new Subject<void>();
  private readonly viewportRuler: ViewportRuler = inject(ViewportRuler);

  private scrollSize: number = 600;

  constructor() {
    effect(() => {
      const scrollable = this.scrollable();
      const tabsList = this.tabsQueryList();

      if (scrollable && tabsList.length > 0) {
        const tabsComponent = tabsList[0];
        // Track tabs in the main effect
        tabsComponent.tabs();

        untracked(() => {
          this.initScrollLogic(scrollable, tabsComponent);
        });
      }
    });
  }

  private initScrollLogic(scrollable: ScrollableDirective, tabsComponent: TabsComponent<T>): void {
    this.scrollHandler();

    scrollable.elementScrolled().pipe(
      takeUntil(this.destroy))
      .subscribe((): void => {
        this.scrollHandler();
      });

    this.viewportRuler.change(TabsScrollComponent.DEBOUNCE_DELAY_MS).pipe(
      takeUntil(this.destroy))
      .subscribe((): void => {
        this.setScrollSize();
        this.scrollHandler();
      });

    outputToObservable(tabsComponent.onStatusChange).pipe(
      takeUntil(this.destroy),
      filter((event: TabStatusChangeEvent<T>): boolean => event.eventType === 'afterSelect'))
      .subscribe((event: TabStatusChangeEvent<T>): void => {
        this.scrollToTabIfNeeded(event.tabId);
      });

    this.setScrollSize();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  next(): void {
    this.scroll(HorizontalScrollDirection.RIGHT);
  }

  previous(): void {
    this.scroll(HorizontalScrollDirection.LEFT);
  }

  isScrollable(): boolean {
    return this.showPrevButton() || this.showNextButton();
  }

  scrollToTabIfNeeded(tabId: T): void {
    const tabs = this.getTabsComponent();
    if (!tabs) return;

    const tab = tabs.tabs().find((component: TabComponent<T>): boolean => component.getTabId() === tabId);
    if (tab !== undefined) {
      const tabRect = tab.getBoundingClientRect();
      const scrollable = this.scrollable();
      if (!scrollable) return;

      const scrollableElementRect = scrollable.getElementRect();
      if (tabRect.right > scrollableElementRect.right && this.showNextButton()) {
        const distance = tabRect.right - scrollableElementRect.left;
        if (distance > this.scrollSize) {
          scrollable.scrollTo({ right: this.getScrollRightEndPoint(tabRect, scrollableElementRect), behavior: 'smooth' });
        }
      } else if (tabRect.left < scrollableElementRect.left && this.showPrevButton()) {
        scrollable.scrollTo({ left: this.getScrollLeftEndPoint(tabRect, scrollableElementRect), behavior: 'smooth' });
      }
    }
  }

  private scrollHandler(): void {
    const scrollable = this.scrollable();
    if (!scrollable) return;

    const leftScrollOffset = scrollable.measureScrollOffset('left');
    const rightScrollOffset = scrollable.measureScrollOffset('right');
    const scrollableActiveBefore = this.isScrollable();

    this.showPrevButton.set(leftScrollOffset > TabsScrollComponent.SCROLL_EDGE_THRESHOLD);
    this.showNextButton.set(rightScrollOffset > TabsScrollComponent.SCROLL_EDGE_THRESHOLD);

    if (scrollableActiveBefore !== this.isScrollable()) {
      this.scrollableChange.emit(this.isScrollable());
    }
  }

  private scroll(direction: HorizontalScrollDirection, scrollOffset: number = undefined): void {
    const scrollable = this.scrollable();
    if (!scrollable) return;

    if (scrollOffset === undefined) {
      scrollOffset = scrollable.measureScrollOffset(direction);
    }

    if (scrollOffset > TabsScrollComponent.SCROLL_EDGE_THRESHOLD) {
      const scrollEndPoint = scrollOffset < this.scrollSize ? 0 : scrollOffset - this.scrollSize;
      if (direction === HorizontalScrollDirection.LEFT) {
        scrollable.scrollTo({ left: scrollEndPoint, behavior: 'smooth' });
      } else {
        scrollable.scrollTo({ right: scrollEndPoint, behavior: 'smooth' });
      }
    }
  }

  private setScrollSize(): void {
    const scrollable = this.scrollable();
    if (!scrollable) return;

    const elementRect = scrollable.getElementRect();
    if (elementRect !== undefined) {
      this.scrollSize = elementRect.width;
    }
  }

  private getScrollRightEndPoint(tabRect: DOMRect, scrollableElementRect: DOMRect): number {
    const scrollable = this.scrollable();
    const scrollOffsetRight = scrollable.measureScrollOffset('right');
    const scrollEndPoint = scrollableElementRect.right + scrollOffsetRight - tabRect.left + tabRect.width - scrollableElementRect.width;
    return scrollEndPoint < 0 ? 0 : scrollEndPoint;
  }

  private getScrollLeftEndPoint(tabRect: DOMRect, scrollableElementRect: DOMRect): number {
    const scrollable = this.scrollable();
    const scrollOffsetLeft = scrollable.measureScrollOffset('left');
    const scrollEndPoint = scrollOffsetLeft - scrollableElementRect.left + tabRect.left - tabRect.width;
    return scrollEndPoint < 0 ? 0 : scrollEndPoint;
  }

  private verifyTabsList(): void {
    const tabsArray = this.tabsQueryList();
    if (tabsArray.length !== 1) {
      throw new Error(`TabsScrollComponent doesn't support multiple nested TabsComponent, the actual size is ${tabsArray.length}`);
    }
  }

  private getTabsComponent(): TabsComponent<T> {
    this.verifyTabsList();
    return this.tabsQueryList()[0];
  }
}
